// Kezdőlap — a Hub belépője, súgó-központ karakterrel.
//
// A lap egyetlen kérdéssel nyit („Mit keresel?"), mert a tudástárba érkező 90%-ban keres, nem
// böngészik. A kereső alatt a leggyakoribb címkék chipjei állnak: ez mutatja meg, MIRŐL szól ez a
// tudástár, mielőtt bárki gépelne. Utána a területek kártyái (a szín a terméket jelöli), végül a
// jogok szerinti belépők és a friss változások.
//
// A hero raszter-háttere az egyetlen díszítő elem az egész felületen — ezért van csak itt.
import {
  BookOpenIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { PERMISSIONS } from '@morgens-hub/shared';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { HomeEntry } from '@/features/home/components/entry-list';
import { EntryList } from '@/features/home/components/entry-list';
import {
  useKnowledgePopularTags,
  useKnowledgeRecent,
  useKnowledgeTree,
} from '@/features/knowledge/api';
import { NoAccess } from '@/features/knowledge/components/no-access';
import { SpaceCard } from '@/features/knowledge/components/space-card';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useHasPermission, usePermissionState } from '@/lib/use-permission';

export const Route = createFileRoute('/')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: HomePage,
});

/** Ennyi terület fér ki a kezdőlapra; a többi a katalógusban van. */
const FEATURED_SPACES = 8;

function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  // Ugyanaz a query-kulcs, amit az app-váz és a jog-hook is használ → nincs plusz kérés.
  const session = api.session.useQuery(['session']);
  const tree = useKnowledgeTree();
  const recent = useKnowledgeRecent();
  const popularTags = useKnowledgePopularTags(6);
  const viewPermission = usePermissionState(PERMISSIONS.articleView);
  const canView = viewPermission.allowed;
  const canWrite = useHasPermission(PERMISSIONS.articleManage);
  const canManageSpaces = useHasPermission(PERMISSIONS.spaceManage);
  const canManageCategories = useHasPermission(PERMISSIONS.categoryManage);

  const sessionBody = session.data?.status === 200 ? session.data.body : null;
  const treeBody = tree.data?.status === 200 ? tree.data.body : null;
  const recentBody = recent.data?.status === 200 ? recent.data.body : null;
  const tagsBody = popularTags.data?.status === 200 ? popularTags.data.body : null;

  const spacesWithContent = useMemo(
    () => (treeBody?.spaces ?? []).filter((s) => s.articleCount > 0),
    [treeBody],
  );
  // A legtöbb cikket tartalmazó területek kerülnek ki — a kezdőlap nem katalógus-másolat.
  const featured = useMemo(
    () => [...spacesWithContent].sort((a, b) => b.articleCount - a.articleCount).slice(0, FEATURED_SPACES),
    [spacesWithContent],
  );
  const totalArticles = spacesWithContent.reduce((n, s) => n + s.articleCount, 0);

  // Csak a keresztnév: a teljes név egy köszönésben túl hivatalos, a bejelentkezési e-mail pedig
  // nem szólítás. Magyar névsorrendben a keresztnév áll hátul; ha egyik claim sincs (dev-token),
  // a cím személytelen marad.
  const firstName = sessionBody?.givenName ?? sessionBody?.name?.split(' ').at(-1) ?? null;

  const entries: HomeEntry[] = [
    {
      to: '/tudastar',
      icon: BookOpenIcon,
      title: t('home.knowledgeTitle'),
      body: t('home.knowledgeBody'),
      ...(treeBody ? { meta: t('knowledge.spaceCount', { count: spacesWithContent.length }) } : {}),
    },
    {
      to: '/sugo',
      icon: LifebuoyIcon,
      title: t('home.helpTitle'),
      body: t('home.helpBody'),
    },
  ];
  if (canWrite) {
    entries.push({
      to: '/uj-cikk',
      icon: PencilSquareIcon,
      title: t('newArticle.title'),
      body: t('home.newArticleBody'),
    });
  }
  if (canManageSpaces || canManageCategories) {
    // A szerkezet nem külön oldal többé: a tudástár szerkesztés-módja. A belépő azért marad, mert a
    // menüpont eltűnt, és a szerkesztés-mód kapcsolóját csak az látja, aki már a katalógusban van.
    entries.push({
      to: '/tudastar',
      search: { szerkesztes: '1' },
      icon: Squares2X2Icon,
      title: t('structure.title'),
      body: t('home.structureBody'),
    });
  }

  if (viewPermission.isLoading) {
    return (
      <AppShell session={sessionBody}>
        <div className="space-y-4" aria-label={t('common.loading')}>
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      </AppShell>
    );
  }

  if (viewPermission.isError && !viewPermission.isForbidden) {
    return (
      <AppShell session={sessionBody}>
        <p className="text-destructive text-sm">{t('knowledge.loadError')}</p>
      </AppShell>
    );
  }

  // Olvasási jog nélkül nincs mit mutatni — de az app-vázon BELÜL maradunk, hogy a felhasználó ki
  // tudjon lépni. (A hookok fölötte futnak le: a korai return nem bontja meg a hook-sorrendet.)
  if (!canView) {
    return (
      <AppShell session={sessionBody}>
        <NoAccess />
      </AppShell>
    );
  }

  return (
    <AppShell session={sessionBody} bleed>
      <section className="border-border relative overflow-hidden border-b">
        <div className="hero-grid pointer-events-none absolute inset-0" aria-hidden />

        <div className="relative mx-auto max-w-3xl px-5 pb-14 pt-16 text-center lg:px-8">
          <h1 className="text-read-3xl font-semibold tracking-tight">
            {firstName ? t('home.heroNamed', { name: firstName }) : t('home.hero')}
          </h1>
          <p className="text-muted-foreground text-read-sm mx-auto mt-3 max-w-xl">
            {treeBody
              ? t('home.heroSubtitle', {
                  articles: totalArticles,
                  spaces: spacesWithContent.length,
                })
              : t('home.heroSubtitleFallback')}
          </p>

          <form
            role="search"
            className="relative mt-8"
            onSubmit={(event) => {
              event.preventDefault();
              const q = query.trim();
              if (q) void navigate({ to: '/kereses', search: { q } });
            }}
          >
            <MagnifyingGlassIcon
              className="text-subtle pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={t('knowledge.searchPlaceholder')}
              aria-label={t('search.placeholder')}
              className="h-14 rounded-2xl pl-14 text-base shadow-sm"
            />
          </form>

          {/* Nem díszlet: a chipek megmutatják, miről szól ez a tudástár — és egy kattintás a
              címke-oldalra visz. A darabszámot elhagyjuk, mert a döntéshez nem kell. */}
          {tagsBody && tagsBody.tags.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <span className="text-subtle text-sm">{t('home.popularTags')}</span>
              {tagsBody.tags.map(({ tag }) => (
                <Link
                  key={tag}
                  to="/tag/$tag"
                  params={{ tag }}
                  className="border-border text-muted-foreground hover:border-primary/50 hover:text-primary rounded-full border px-3 py-1 text-sm transition-colors duration-150"
                >
                  {tag}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <section>
          <div className="mb-4 flex items-baseline justify-between gap-4">
            <h2 className="text-subtle text-2xs font-medium uppercase tracking-[0.14em]">
              {t('home.spaces')}
            </h2>
            <Link
              to="/tudastar"
              className="text-muted-foreground hover:text-primary text-sm transition-colors duration-150"
            >
              {t('home.allSpaces', { count: spacesWithContent.length })}
            </Link>
          </div>

          {tree.isLoading && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Skeleton key={i} className="h-44 w-full rounded-xl" />
              ))}
            </div>
          )}

          {featured.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {featured.map((space) => (
                <SpaceCard key={space.id} space={space} />
              ))}
            </div>
          )}

          {!tree.isLoading && !treeBody && (
            <p className="text-destructive text-sm">{t('knowledge.loadError')}</p>
          )}

          {treeBody && featured.length === 0 && (
            <p className="text-muted-foreground text-sm">{t('knowledge.emptyHome')}</p>
          )}
        </section>

        <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_18rem]">
          <section>
            <h2 className="text-subtle text-2xs mb-3 font-medium uppercase tracking-[0.14em]">
              {t('home.entries')}
            </h2>
            <EntryList entries={entries} label={t('home.entries')} />
          </section>

          <section>
            <h2 className="text-subtle text-2xs font-medium uppercase tracking-[0.14em]">
              {t('knowledge.recent')}
            </h2>

            {recent.isLoading && (
              <div className="mt-4 space-y-4">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            )}

            {recentBody && recentBody.articles.length > 0 && (
              <ul className="mt-4 space-y-4">
                {/* Hatnál több már oszlop-hossz, nem „mi történt mostanában". */}
                {recentBody.articles.slice(0, 6).map((article) => (
                  <li key={article.id}>
                    <Link
                      to="/hub/$spaceSlug/$categorySlug/$articleSlug"
                      params={{
                        spaceSlug: article.spaceSlug,
                        categorySlug: article.categorySlug,
                        articleSlug: article.slug,
                      }}
                      className="group block"
                    >
                      <span className="group-hover:text-primary block text-sm font-medium leading-snug transition-colors duration-150">
                        {article.title}
                      </span>
                      <span className="text-subtle mt-0.5 block text-xs">
                        {article.spaceName} <span aria-hidden>›</span> {article.categoryName}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {!recent.isLoading && !recentBody && (
              <p className="text-destructive mt-4 text-sm">{t('knowledge.loadError')}</p>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  );
}
